import React, { useState, useRef, useMemo } from 'react';
import StravaLogo from '@strava/ui/StravaLogo';
import {
  getBrowser,
  getMobileOS,
  getParamFromURL,
  isUniversalLinkPage
} from '@strava/utils';
import SmartBanner from '@strava/smart-banner-ui';
import { EXPERIMENT_COHORTS, SUPPORTED_BROWSERS } from '@strava/constants';
import {
  useStravaBranch,
  useStravaExperiment
} from '@strava/container-context';
import { useSignUpModalContext } from '@strava/authentication-ui';
import Button from '@strava/ui/Button';
import { useFrontDoorsPageContainerContext } from '../../../../state/FrontDoorsPageContainerContext';
import IconButton from '../../../IconButton';
import CallToAction from '../../../CallToAction';
import MenuDrawer from './components/MenuDrawer';
import MenuIcon from './components/MenuIcon';

import styles from './MobileNav.module.scss';
import { isNavSignUpVariantA } from '../../../../shared/pageUtils';

const MobileNav = () => {
  const [isMenuOpen, toggleMenu] = useState(false);
  const mobileAppDownloadBannerRef = useRef();
  const {
    t,
    state,
    onMobileHeaderLogoClick,
    onMobileHeaderMenuOpen,
    onMobileHeaderMenuClose,
    onMobileHeaderSignupBtnClick
  } = useFrontDoorsPageContainerContext();
  const { openSignUpModal } = useSignUpModalContext();

  const { page, category, userAgent, showSmartBanner, branch, url } = state;

  const { hasApp, appLink, isProcessing } = useStravaBranch();
  const { experiment } = useStravaExperiment();

  // https://strava.atlassian.net/wiki/spaces/TG/pages/2194506285/16+LOH+Mobile+Web+Sign+Up+vs+Get+App+Mobile
  const isWebAppExpVariant = (variantList) =>
    experiment?.name === 'loh-signup-vs-get-app' &&
    variantList.includes(experiment?.cohort);

  const handleLogoClick = () => {
    onMobileHeaderLogoClick();
    window.location.href = '/';
  };

  const handleMenuToggle = () => {
    if (isMenuOpen) {
      onMobileHeaderMenuClose();
    } else {
      onMobileHeaderMenuOpen();
    }
    toggleMenu(!isMenuOpen);
  };

  // For development only - use query param to force the smartbanner to show
  // on a page.
  const forceSmartBanner = useMemo(
    () => !!getParamFromURL(url, 'force_smartbanner'),
    [url]
  );

  // mobile safari only: do not show smartbanner if athlete has app (as best as branch can tell)
  // and is on a page that matches an apple app link path (pages that match this path automatically get
  // the native ios smartbanner if the user has the app installed. https://developer.apple.com/forums/thread/105129)
  const showOnSafari = useMemo(() => {
    if (getBrowser(userAgent) === SUPPORTED_BROWSERS.safari && hasApp) {
      const { pathname } = new URL(url);
      return !isUniversalLinkPage(pathname);
    }
    return true;
  }, [hasApp, url, userAgent]);

  return (
    <>
      {((showSmartBanner &&
        showOnSafari &&
        !isWebAppExpVariant([EXPERIMENT_COHORTS.variantA])) ||
        forceSmartBanner) && (
        <SmartBanner
          mobileOS={getMobileOS(userAgent)}
          page={page}
          category={category}
          branch={branch}
          ref={mobileAppDownloadBannerRef}
        />
      )}
      <div className={styles.container}>
        <div className={styles.left}>
          <IconButton
            className={styles.menuBtn}
            onClick={handleMenuToggle}
            ariaLabel={
              isMenuOpen ? t('header.alt.close') : t('header.alt.close')
            }
            data-cy="mobile-nav-drawer-menu-btn"
            style={{
              top: mobileAppDownloadBannerRef.current?.getBoundingClientRect()
                .height
            }}
          >
            <MenuIcon isOpen={isMenuOpen} />
          </IconButton>
          <div className={styles.logo}>
            <IconButton onClick={handleLogoClick} ariaLabel="Strava">
              <StravaLogo
                id="fdpc-mobile-nav-strava-logo"
                data-cy="strava-logo"
                width={95}
                height={20}
                alt=""
              />
            </IconButton>
          </div>
        </div>

        <div className={styles.right}>
          {isNavSignUpVariantA(experiment) ? (
            <Button
              className={styles.signupBtn}
              type="button"
              onClick={() => openSignUpModal('generic')}
              data-cy="signup_btn"
              variant="primary"
            >
              {t(`header.join_us`)}
            </Button>
          ) : (
            <CallToAction
              href={
                isWebAppExpVariant([
                  EXPERIMENT_COHORTS.variantA,
                  EXPERIMENT_COHORTS.variantB
                ])
                  ? '/register/free'
                  : appLink
              }
              isProcessing={
                isWebAppExpVariant([
                  EXPERIMENT_COHORTS.variantA,
                  EXPERIMENT_COHORTS.variantB
                ])
                  ? false
                  : isProcessing
              }
              element="get_app"
              className={styles.headerCallToAction}
              label={
                isWebAppExpVariant([
                  EXPERIMENT_COHORTS.variantA,
                  EXPERIMENT_COHORTS.variantB
                ])
                  ? t('header.join_us')
                  : t('header.get_the_app')
              }
              dataCy="signup_btn_mobile"
              onClick={onMobileHeaderSignupBtnClick}
            />
          )}
        </div>
        <MenuDrawer
          isOpen={isMenuOpen}
          onClose={() => toggleMenu(!isMenuOpen)}
          top={
            mobileAppDownloadBannerRef.current?.getBoundingClientRect().height
          }
        />
      </div>
    </>
  );
};

export default MobileNav;
